import { ParcelRole } from './phaseParcelsApi';
import { ParcelBrw, brwTooltipLines } from '../heatmap/brwScale';

/**
 * What a parcel says when the pointer rests on it.
 *
 * An outline on a map is anonymous. Which Flurstück it is, how big it is and whether it counts
 * towards the plot are three questions a reader has while looking at the shape, and the answers used
 * to live in a table somewhere else — or, once the table was replaced by the map, nowhere.
 *
 * One function for all three maps in the dashboard: the small one beside the property, the one in
 * the drill-down and the one in the assignment dialog. They draw the same parcels from the same
 * rows, and three separately-worded tooltips would eventually disagree about the same plot.
 *
 * Everything is optional because the sources differ. A parcel of the neighbourhood comes from the
 * cadastre with no assessment behind it, so it has a number and an area and no state; a row from an
 * estimate has all three. A missing field is left out rather than shown as a dash — a tooltip is
 * read in a glance, and empty rows in it cost more than they say.
 */

export interface ParcelHoverSource {
    number?: string | null;
    gemarkung?: string | null;
    flur?: string | null;
    areaM2?: number | null;
    role?: ParcelRole;
    /** What the estimator recorded for or against it. */
    reasons?: string[];
    /**
     * Der Bodenrichtwert, wie er am Flurstück steht.
     *
     * Er stand bisher nur in der Heatmap. Die Frage „was ist der Boden hier wert?" stellt sich aber
     * gerade dort, wo eine Liegenschaft betrachtet wird — und dort standen dieselben Flurstücke,
     * nur ohne ihren Wert.
     */
    brw?: ParcelBrw | null;
}

export interface ParcelHoverText {
    /** The heading: the parcel number, or a plain word where the register carries none. */
    title: string;
    /** Gemarkung and Flur, joined — the parcel number is only unique within them. */
    place: string | null;
    /** Formatted with the reader's separators, or null where no area is recorded. */
    area: string | null;
    /** Translation key for the state, or null for a parcel that has none. */
    roleKey: string | null;
    /** The estimator's reasons, at most two: a tooltip is not a report. */
    reasons: string[];
    /**
     * Der Bodenrichtwert in Worten, oder null.
     *
     * Formatiert von derselben Funktion wie in der Heatmap. Eine zweite Fassung wäre eine zweite
     * Auskunft über dieselbe Zahl — und die beiden gingen auseinander, sobald jemand eine von
     * beiden anfasst.
     */
    landValue: string | null;
}

const ROLE_KEY: Record<ParcelRole, string> = {
    core: 'portfolioDashboard.parcels.core',
    manual: 'portfolioDashboard.parcels.manual',
    accepted: 'portfolioDashboard.parcels.accepted',
    open: 'portfolioDashboard.parcels.open',
    rejected: 'portfolioDashboard.parcels.rejected',
};

/** How many reasons fit before the tooltip stops being read. */
const MAX_REASONS = 2;

export const parcelHoverText = (
    parcel: ParcelHoverSource,
    /** Injected so this stays free of i18n and of the browser's idea of a locale. */
    formatArea: (value: number) => string,
    /**
     * The words, translated by the caller. Same reason as the formatter: a rule about what a parcel
     * says belongs in one place, and the language it says it in belongs in the locale files.
     *
     * `parcelLabel` does double duty — the word before the number, and the whole heading where the
     * register carries no number at all.
     */
    parcelLabel: string,
    districtLabel: string,
    sectionLabel: string,
    /**
     * Wie ein Eurobetrag geschrieben wird — aus demselben Grund hereingereicht wie die Fläche.
     *
     * Ohne Formatierer kein Bodenrichtwert: Eine Zahl ohne Tausendertrennung ist an dieser Stelle
     * schlechter zu lesen als gar keine, und raten soll diese Funktion nicht.
     */
    formatEuro?: (value: number) => string,
): ParcelHoverText => ({
    title: parcel.number ? `${parcelLabel} ${parcel.number}` : parcelLabel,
    place:
        [
            parcel.gemarkung ? `${districtLabel} ${parcel.gemarkung}` : null,
            parcel.flur ? `${sectionLabel} ${parcel.flur}` : null,
        ]
            .filter(Boolean)
            .join(', ') || null,
    // Zero is not an area but a missing measurement: the cadastre writes it where it has none, and
    // "0 m²" beside an outline that plainly covers ground reads as a bug.
    area: Number.isFinite(parcel.areaM2) && (parcel.areaM2 as number) > 0
        ? `${formatArea(Math.round(parcel.areaM2 as number))} m²`
        : null,
    roleKey: parcel.role ? ROLE_KEY[parcel.role] : null,
    reasons: (parcel.reasons ?? []).filter(Boolean).slice(0, MAX_REASONS),
    /**
     * Dieselbe Formatierung wie in der Heatmap — sie erklärt auch, *wie* der Wert zustande kam:
     * flächengewichtet, nach GFZ, oder als Spanne bei deckungsgleichen Zonen. Eine nackte Zahl
     * verschwiege, dass es manchmal keine einzige gibt.
     */
    landValue: formatEuro
        ? brwTooltipLines(parcel.brw, formatEuro).join(' · ') || null
        : null,
});
